import React, { useMemo } from 'react';
import { BrainIcon, AlertTriangleIcon, CalendarCheckIcon, TrendingUpIcon, UserXIcon, ClockIcon, BellIcon } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { useData } from '../../contexts/DataContext';
import { getToday } from '../../utils/helpers';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
export function Intelligence() {
  const {
    appointments,
    patients
  } = useData();
  // 1. No-Show Prediction
  const noShowData = useMemo(() => {
    return patients.map(patient => {
      const patientApts = appointments.filter(a => a.patientId === patient.id);
      const total = patientApts.length;
      const completed = patientApts.filter(a => a.status === 'completada').length;
      const cancelled = patientApts.filter(a => a.status === 'cancelada').length;
      const rate = total > 0 ? cancelled / total * 100 : 0;
      let riskLevel = 'Baja';
      let riskColor = 'success' as 'success' | 'warning' | 'danger';
      if (rate > 40) {
        riskLevel = 'Alta';
        riskColor = 'danger';
      } else if (rate >= 20) {
        riskLevel = 'Media';
        riskColor = 'warning';
      }
      return {
        ...patient,
        totalApts: total,
        completedApts: completed,
        cancelledApts: cancelled,
        cancelRate: rate,
        riskLevel,
        riskColor
      };
    }).filter(p => p.totalApts > 0).sort((a, b) => b.cancelRate - a.cancelRate);
  }, [appointments, patients]);
  const highRiskPatients = noShowData.filter(p => p.riskLevel === 'Alta');
  // 2. Best Schedule Suggestion
  const scheduleData = useMemo(() => {
    const completedApts = appointments.filter(a => a.status === 'completada');
    const dayCounts: Record<number, number> = {
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0,
      6: 0,
      0: 0
    };
    const hourCounts: Record<number, number> = {};
    completedApts.forEach(apt => {
      const date = new Date(apt.date);
      // Adjust for timezone offset to get correct local day
      const day = new Date(date.getTime() + date.getTimezoneOffset() * 60000).getDay();
      dayCounts[day] = (dayCounts[day] || 0) + 1;
      if (apt.time) {
        const hour = parseInt(apt.time.split(':')[0], 10);
        hourCounts[hour] = (hourCounts[hour] || 0) + 1;
      }
    });
    const daysMap = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
    const bestDays = Object.entries(dayCounts).map(([day, count]) => ({
      day: daysMap[parseInt(day)],
      count
    })).filter(d => d.day !== 'Dom') // Usually closed on Sundays
    .sort((a, b) => b.count - a.count);
    const bestHours = Object.entries(hourCounts).map(([hour, count]) => ({
      hour: `${hour}:00`,
      count
    })).sort((a, b) => b.count - a.count).slice(0, 5);
    return {
      bestDays,
      bestHours
    };
  }, [appointments]);
  // 3. Smart Alerts
  const alerts = useMemo(() => {
    const generatedAlerts: Array<{
      id: string;
      type: 'danger' | 'warning' | 'info';
      title: string;
      message: string;
      icon: typeof ClockIcon;
    }> = [];
    const todayStr = getToday(); // YYYY-MM-DD string
    // Helper: calculate days between two YYYY-MM-DD strings
    const daysBetween = (dateA: string, dateB: string): number => {
      const a = new Date(dateA + 'T12:00:00');
      const b = new Date(dateB + 'T12:00:00');
      return Math.round(Math.abs(a.getTime() - b.getTime()) / (1000 * 60 * 60 * 24));
    };
    // Helper: date string N days ago
    const daysAgo = (n: number): string => {
      const d = new Date(todayStr + 'T12:00:00');
      d.setDate(d.getDate() - n);
      return d.toISOString().split('T')[0];
    };
    const thirtyDaysAgo = daysAgo(30);
    const sixtyDaysAgo = daysAgo(60);
    const ninetyDaysAgo = daysAgo(90);
    // --- Alert 1: Overall high cancellation rate in last 30 days ---
    const recentApts = appointments.filter(a => a.date >= thirtyDaysAgo && a.date <= todayStr);
    const recentTotal = recentApts.length;
    const recentCancelled = recentApts.filter(a => a.status === 'cancelada').length;
    const recentCancelRate = recentTotal > 0 ? recentCancelled / recentTotal * 100 : 0;
    if (recentTotal >= 5 && recentCancelRate > 15) {
      generatedAlerts.push({
        id: 'high-cancellations-30d',
        type: 'danger',
        title: 'Alta Tasa de Cancelaciones Recientes',
        message: `En los últimos 30 días: ${recentCancelled} de ${recentTotal} citas fueron canceladas (${recentCancelRate.toFixed(1)}%). Considere implementar confirmaciones automáticas.`,
        icon: AlertTriangleIcon
      });
    }
    // --- Alert 2: Patients with consecutive cancellations (last 2 or 3) ---
    patients.forEach(patient => {
      const patientApts = appointments.filter(a => a.patientId === patient.id).sort((a, b) => b.date > a.date ? 1 : b.date < a.date ? -1 : 0);
      if (patientApts.length >= 2) {
        const last2 = patientApts.slice(0, 2);
        const last2Cancelled = last2.every(a => a.status === 'cancelada');
        if (last2Cancelled) {
          const isLast3 = patientApts.length >= 3 && patientApts[2].status === 'cancelada';
          generatedAlerts.push({
            id: `consecutive-cancel-${patient.id}`,
            type: 'danger',
            title: isLast3 ? 'Cancelaciones Consecutivas (3+)' : 'Cancelaciones Consecutivas',
            message: `${patient.name} ha cancelado sus últimas ${isLast3 ? '3 o más' : '2'} citas consecutivas. Se recomienda contactar al paciente.`,
            icon: UserXIcon
          });
        }
      }
    });
    // --- Alert 3: Inactive frequent patients (no visit in 90+ days) ---
    patients.forEach(patient => {
      const completedApts = appointments.filter(a => a.patientId === patient.id && a.status === 'completada').sort((a, b) => b.date > a.date ? 1 : b.date < a.date ? -1 : 0);
      if (completedApts.length >= 2) {
        const lastVisitDate = completedApts[0].date;
        const days = daysBetween(lastVisitDate, todayStr);
        if (days > 90) {
          generatedAlerts.push({
            id: `inactive-${patient.id}`,
            type: 'warning',
            title: 'Paciente Frecuente Inactivo',
            message: `${patient.name} tenía ${completedApts.length} visitas completadas pero no ha regresado en ${days} días. Última visita: ${lastVisitDate}.`,
            icon: ClockIcon
          });
        }
      }
    });
    // --- Alert 4: New patient who never returned (1 completed apt, 30+ days ago) ---
    patients.forEach(patient => {
      const patientApts = appointments.filter(a => a.patientId === patient.id);
      const completedApts = patientApts.filter(a => a.status === 'completada');
      const futureApts = patientApts.filter(a => a.date > todayStr && a.status === 'programada');
      if (completedApts.length === 1 && futureApts.length === 0) {
        const days = daysBetween(completedApts[0].date, todayStr);
        if (days > 30) {
          generatedAlerts.push({
            id: `no-return-${patient.id}`,
            type: 'warning',
            title: 'Paciente Nuevo Sin Retorno',
            message: `${patient.name} tuvo su primera cita hace ${days} días pero no ha agendado una nueva visita.`,
            icon: UserXIcon
          });
        }
      }
    });
    // --- Alert 5: High-risk patients with upcoming appointments (suggest double confirmation) ---
    const highRiskIds = new Set(noShowData.filter(p => p.riskLevel === 'Alta').map(p => p.id));
    const upcomingHighRisk = appointments.filter(a => a.date >= todayStr && a.status === 'programada' && highRiskIds.has(a.patientId));
    if (upcomingHighRisk.length > 0) {
      const patientNames = [...new Set(upcomingHighRisk.map(a => {
        const p = patients.find(p => p.id === a.patientId);
        return p?.name || 'Desconocido';
      }))].slice(0, 3);
      generatedAlerts.push({
        id: 'upcoming-high-risk',
        type: 'danger',
        title: 'Citas Próximas de Alto Riesgo',
        message: `${upcomingHighRisk.length} cita(s) programada(s) de pacientes con alto riesgo de cancelación: ${patientNames.join(', ')}${patientNames.length < upcomingHighRisk.length ? '...' : ''}. Se recomienda doble confirmación.`,
        icon: AlertTriangleIcon
      });
    }
    // Sort: danger first, then warning, then info
    const typePriority = {
      danger: 0,
      warning: 1,
      info: 2
    };
    generatedAlerts.sort((a, b) => typePriority[a.type] - typePriority[b.type]);
    return generatedAlerts;
  }, [appointments, patients, noShowData]);
  return <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-2888">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-2889">
        <div data-id="element-2890">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-2891">
            <BrainIcon className="w-8 h-8 text-indigo-600 dark:text-indigo-400" data-id="element-2892" />
            Inteligencia y Analítica
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1" data-id="element-2893">
            Insights predictivos basados en el comportamiento de sus pacientes
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2894">
        {/* Section 1: No-Show Prediction */}
        <Card className="lg:col-span-2 flex flex-col" data-id="element-2895">
          <CardHeader data-id="element-2896">
            <CardTitle className="flex items-center gap-2" data-id="element-2897">
              <UserXIcon className="w-5 h-5 text-rose-500" data-id="element-2898" />
              Predicción de No-Show (Riesgo de Cancelación)
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col" data-id="element-2899">
            <div className="mb-4 flex items-center justify-between bg-rose-50 dark:bg-rose-900/20 p-4 rounded-xl border border-rose-100 dark:border-rose-800/30" data-id="element-2900">
              <div data-id="element-2901">
                <p className="text-sm text-rose-600 dark:text-rose-400 font-medium" data-id="element-2902">
                  Pacientes en Riesgo Alto
                </p>
                <p className="text-2xl font-bold text-rose-700 dark:text-rose-300" data-id="element-2903">
                  {highRiskPatients.length}
                </p>
              </div>
              <Badge variant="danger" className="text-sm px-3 py-1" data-id="element-2904">
                &gt; 40% de cancelaciones
              </Badge>
            </div>

            <div className="flex-1 overflow-auto max-h-[300px] pr-2" data-id="element-2905">
              <div className="space-y-3" data-id="element-2906">
                {noShowData.slice(0, 10).map(patient => <div key={patient.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-100 dark:border-gray-700" data-id="element-2907">
                    <div data-id="element-2908">
                      <p className="font-medium text-gray-900 dark:text-white" data-id="element-2909">
                        {patient.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2910">
                        {patient.cancelledApts} canceladas de{' '}
                        {patient.totalApts} citas
                      </p>
                    </div>
                    <div className="flex items-center gap-3" data-id="element-2911">
                      <span className="text-sm font-bold text-gray-700 dark:text-gray-300" data-id="element-2912">
                        {patient.cancelRate.toFixed(0)}%
                      </span>
                      <Badge variant={patient.riskColor} data-id="element-2913">
                        {patient.riskLevel}
                      </Badge>
                    </div>
                  </div>)}
                {noShowData.length === 0 && <p className="text-center text-gray-500 py-4" data-id="element-2914">
                    No hay datos suficientes para calcular el riesgo.
                  </p>}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 3: Smart Alerts */}
        <Card className="flex flex-col" data-id="element-2915">
          <CardHeader data-id="element-2916">
            <CardTitle className="flex items-center gap-2" data-id="element-2917">
              <BellIcon className="w-5 h-5 text-amber-500" data-id="element-2918" />
              Alertas Inteligentes
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 overflow-auto max-h-[400px]" data-id="element-2919">
            <div className="space-y-4" data-id="element-2920">
              {alerts.length > 0 ? alerts.map(alert => {
              const Icon = alert.icon;
              return <div key={alert.id} className={`p-4 rounded-xl border ${alert.type === 'danger' ? 'bg-red-50 border-red-100 dark:bg-red-900/20 dark:border-red-800/30' : 'bg-amber-50 border-amber-100 dark:bg-amber-900/20 dark:border-amber-800/30'}`} data-id="element-2921">
                      <div className="flex items-start gap-3" data-id="element-2922">
                        <div className={`p-2 rounded-lg ${alert.type === 'danger' ? 'bg-red-100 text-red-600 dark:bg-red-900/50 dark:text-red-400' : 'bg-amber-100 text-amber-600 dark:bg-amber-900/50 dark:text-amber-400'}`} data-id="element-2923">
                          <Icon className="w-5 h-5" data-id="element-2924" />
                        </div>
                        <div data-id="element-2925">
                          <h4 className={`font-semibold text-sm ${alert.type === 'danger' ? 'text-red-900 dark:text-red-200' : 'text-amber-900 dark:text-amber-200'}`} data-id="element-2926">
                            {alert.title}
                          </h4>
                          <p className={`text-xs mt-1 leading-relaxed ${alert.type === 'danger' ? 'text-red-700 dark:text-red-300' : 'text-amber-700 dark:text-amber-300'}`} data-id="element-2927">
                            {alert.message}
                          </p>
                        </div>
                      </div>
                    </div>;
            }) : <div className="text-center py-8 text-gray-500" data-id="element-2928">
                  <BellIcon className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" data-id="element-2929" />
                  <p data-id="element-2930">No hay alertas activas en este momento.</p>
                </div>}
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Best Schedule Suggestion */}
        <Card className="lg:col-span-3" data-id="element-2931">
          <CardHeader data-id="element-2932">
            <CardTitle className="flex items-center gap-2" data-id="element-2933">
              <CalendarCheckIcon className="w-5 h-5 text-emerald-500" data-id="element-2934" />
              Mejor Horario Sugerido
            </CardTitle>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-2935">
              Basado en el histórico de citas completadas exitosamente
            </p>
          </CardHeader>
          <CardContent data-id="element-2936">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2937">
              <div data-id="element-2938">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2" data-id="element-2939">
                  <TrendingUpIcon className="w-4 h-4 text-emerald-500" data-id="element-2940" />
                  Días con Mayor Asistencia
                </h4>
                <div className="h-[250px]" data-id="element-2941">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-2942">
                    <BarChart data={scheduleData.bestDays} data-id="element-2943">
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" data-id="element-2944" />
                      <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{
                      fill: '#6b7280',
                      fontSize: 12
                    }} data-id="element-2945" />
                      <YAxis axisLine={false} tickLine={false} tick={{
                      fill: '#6b7280',
                      fontSize: 12
                    }} data-id="element-2946" />
                      <Tooltip cursor={{
                      fill: '#f3f4f6'
                    }} contentStyle={{
                      borderRadius: '8px',
                      border: 'none',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }} formatter={(value: number) => [value, 'Citas completadas']} labelFormatter={label => `Día: ${label}`} data-id="element-2947" />
                      <Bar dataKey="count" name="Citas completadas" radius={[4, 4, 0, 0]} data-id="element-2948">
                        {scheduleData.bestDays.map((entry, index) => <Cell key={`cell-${index}`} fill={index === 0 ? '#10b981' : '#6ee7b7'} data-id="element-2949" />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div data-id="element-2950">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2" data-id="element-2951">
                  <ClockIcon className="w-4 h-4 text-sky-500" data-id="element-2952" />
                  Horas Pico de Asistencia
                </h4>
                <div className="space-y-4" data-id="element-2953">
                  {scheduleData.bestHours.map((hour, index) => {
                  const maxCount = Math.max(...scheduleData.bestHours.map(h => h.count));
                  const percentage = hour.count / maxCount * 100;
                  return <div key={hour.hour} className="relative" data-id="element-2954">
                        <div className="flex justify-between text-sm mb-1" data-id="element-2955">
                          <span className="font-medium text-gray-700 dark:text-gray-300" data-id="element-2956">
                            {hour.hour}
                          </span>
                          <span className="text-gray-500 dark:text-gray-400" data-id="element-2957">
                            {hour.count} citas
                          </span>
                        </div>
                        <div className="w-full bg-gray-100 dark:bg-gray-800 rounded-full h-2.5" data-id="element-2958">
                          <div className="bg-sky-500 h-2.5 rounded-full transition-all duration-500" style={{
                        width: `${percentage}%`
                      }} data-id="element-2959"></div>
                        </div>
                      </div>;
                })}
                  {scheduleData.bestHours.length === 0 && <p className="text-center text-gray-500 py-4" data-id="element-2960">
                      No hay datos suficientes de horarios.
                    </p>}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>;
}